import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';

import { useGetRacesQuery, useGetFeedRacesQuery } from '../api/apiSlice';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { useAppSelector } from '../app/hooks';
import { groupByCalendarDay } from '../utils/dateGrouping';
import { Race, ClubBadge } from '../types/api';
import RaceFormDialog from './RaceFormDialog';
import ViewModeToggle, { ViewMode } from './ViewModeToggle';
import DayGroupHeader from './DayGroupHeader';
import FeedCard from './FeedCard';
import FeedCardSkeleton from './FeedCardSkeleton';

type RaceCard = Race & Partial<ClubBadge>;

// This page shows one club's public races and has no per-visitor club
// context (no login required) -- REACT_APP_DEFAULT_CLUB_ID is this
// deployment's "home" club until public multi-club browsing exists (see
// the plan's explicitly-scoped-out follow-ups).
const DEFAULT_CLUB_ID = process.env.REACT_APP_DEFAULT_CLUB_ID ?? '';

function Races() {
    const { t, i18n } = useTranslation();
    const sportTypeLabel = useSportTypeLabel();
    const clubID = DEFAULT_CLUB_ID;
    const token = useAppSelector((state) => state.auth.token);
    // Logged-out visitors see this deployment's "home" club; logged-in
    // members see races across every club they belong to, each badged.
    const { data: publicRaces = [], isLoading: isLoadingPublic } = useGetRacesQuery(clubID, { skip: !clubID || !!token });
    const { data: memberRaces = [], isLoading: isLoadingMember } = useGetFeedRacesQuery(undefined, { skip: !token });
    const races: RaceCard[] = token ? memberRaces : publicRaces;
    const isLoading = token ? isLoadingMember : isLoadingPublic;
    const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
    const [suggestionSubmitted, setSuggestionSubmitted] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('date');

    const dayGroups = useMemo(() => {
        if (viewMode !== 'weekday') return [];
        const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
        return groupByCalendarDay(races, (race) => new Date(race.DATE), locale, t('common.unscheduled'));
    }, [races, viewMode, i18n.language, t]);

    const renderCard = (race: RaceCard) => (
        <Grid item xs={12} sm={6} md={4} key={race.ID}>
            <FeedCard
                href={`/races/${race.ID}`}
                image={race.PRESENTATION_IMAGE}
                clubName={race.CLUB_NAME}
                clubLogo={race.CLUB_LOGO}
                sportType={race.SPORT_TYPE}
                date={race.DATE}
                title={race.TITLE}
                subtitle={race.LOCATION ?? undefined}
                stats={[sportTypeLabel(race.SPORT_TYPE)]}
            />
        </Grid>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="h4" gutterBottom>
                    {t('races.title')}
                </Typography>
                {token && (
                    <Button variant="outlined" color="primary" onClick={() => setSuggestDialogOpen(true)}>
                        {t('races.suggest')}
                    </Button>
                )}
            </Box>

            {!isLoading && races.length > 0 && (
                <Box sx={{ marginBottom: 2 }}>
                    <ViewModeToggle value={viewMode} onChange={setViewMode} />
                </Box>
            )}

            {viewMode === 'weekday' && !isLoading && races.length > 0 ? (
                dayGroups.map((group) => (
                    <div key={group.key}>
                        <DayGroupHeader label={group.label} count={group.items.length} />
                        <Grid container spacing={2}>
                            {group.items.map(renderCard)}
                        </Grid>
                    </div>
                ))
            ) : (
                <Grid container spacing={2}>
                    {isLoading &&
                        Array.from({ length: 3 }).map((_, index) => (
                            <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                                <FeedCardSkeleton />
                            </Grid>
                        ))}

                    {!isLoading && races.length === 0 && (
                        <Grid item xs={12}>
                            <Typography color="text.secondary">{t('races.empty')}</Typography>
                        </Grid>
                    )}

                    {!isLoading && races.map(renderCard)}
                </Grid>
            )}

            {suggestDialogOpen && (
                <RaceFormDialog
                    onClose={() => setSuggestDialogOpen(false)}
                    onSuccess={() => setSuggestionSubmitted(true)}
                />
            )}
            <Snackbar
                open={suggestionSubmitted}
                autoHideDuration={5000}
                onClose={() => setSuggestionSubmitted(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSuggestionSubmitted(false)} severity="success" variant="filled">
                    {t('races.suggestionSubmitted')}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default Races;
